import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { isDeletedRecord, useAuditHighlight } from "@/lib/auditHighlight";
import { AuditGhostRecord } from "@/components/admin/AuditGhostRecord";
import {
  listNotifications,
  markNotificationRead,
  readAllNotifications,
} from "@/services/api/notifications";
import type { Notification } from "@/services/api/types";
import { cn } from "@/lib/utils";

const SEVERITY_VARIANT: Record<string, "secondary" | "warning" | "destructive" | "default"> = {
  info: "secondary",
  warning: "warning",
  error: "destructive",
  critical: "destructive",
};

function NotificationListItem({ item, highlighted }: { item: Notification; highlighted: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const markRead = useMutation({
    mutationFn: () => markNotificationRead(item.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card
      className={cn(
        item.read_at === null && "border-primary/40",
        highlighted && "audit-record-box",
      )}
    >
      <CardContent className="flex items-start justify-between gap-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={SEVERITY_VARIANT[item.severity] ?? "secondary"}>
              {item.severity}
            </Badge>
            <span className="text-sm font-medium">
              {t(item.title_key, { ...item.params, defaultValue: item.title_key })}
            </span>
            {item.occurrence_count > 1 ? (
              <Badge variant="outline">×{item.occurrence_count}</Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(item.body_key, { ...item.params, defaultValue: item.body_key })}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {new Date(item.created_at).toLocaleString()}
          </p>
        </div>
        {item.read_at === null ? (
          <Button size="sm" variant="ghost" onClick={() => markRead.mutate()}>
            {t("notifications.markRead")}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Full notification centre for the current principal. */
export function NotificationsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const pageSize = 25;
  // Audit deep-link context (?r=<id>&a=<action>&f=<fields>&ar=<auditId>).
  const audit = useAuditHighlight();
  const ghostRecord = isDeletedRecord(audit);

  const query = useQuery({
    queryKey: ["notifications", offset],
    queryFn: () => listNotifications(pageSize, offset),
  });

  const readAll = useMutation({
    mutationFn: () => readAllNotifications(),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["notifications"] }),
    onError: (error) => toast.error(errorMessage(error)),
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("notifications.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("notifications.subtitle")}</p>
        </div>
        <Button variant="outline" onClick={() => readAll.mutate()} disabled={readAll.isPending}>
          <CheckCheck className="h-4 w-4" />
          {t("notifications.readAll")}
        </Button>
      </div>

      {ghostRecord ? (
        <AuditGhostRecord
          entityType={audit.entityType ?? "notification"}
          entityId={audit.recordId}
        />
      ) : null}

      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : items.length === 0 && !ghostRecord ? (
        <EmptyState title={t("notifications.empty")} />
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <NotificationListItem
              key={item.id}
              item={item}
              highlighted={String(item.id) === audit.recordId}
            />
          ))}
        </div>
      )}

      {total > pageSize ? (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset((value) => Math.max(0, value - pageSize))}
          >
            {t("common.back")}
          </Button>
          <span>
            {offset + 1}–{Math.min(offset + pageSize, total)} / {total}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={offset + pageSize >= total}
            onClick={() => setOffset((value) => value + pageSize)}
          >
            {t("common.next")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
